import React, { useState, useMemo } from 'react';
import RTModal from '../common/RTModal';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import Table from '../common/Table';
import { apiPost } from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import { withRoomGroupKey, EB_BILL_ROW_GROUP_CONFIG } from './ebBillTableUtils';
import './EBChargePreviewModal.css';

const formatAmount = (amount) => (
  `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
);

const PREVIEW_PAGE_SIZE = 50;

const previewColumns = [
  { Header: 'Building', accessor: 'building_name' },
  { Header: 'Room', accessor: 'room_name', Cell: ({ value }) => value || 'N/A' },
  { Header: 'Invoice #', accessor: 'invoice_number' },
  { Header: 'PG Name', accessor: 'pg_name' },
  { Header: 'Room Type', accessor: 'room_type' },
  {
    Header: 'Old Amount',
    accessor: 'current_amount',
    Cell: ({ value }) => formatAmount(value),
  },
  {
    Header: 'EB Amount',
    accessor: 'eb_amount',
    Cell: ({ value }) => formatAmount(value),
  },
  {
    Header: 'New Amount',
    accessor: 'new_amount',
    Cell: ({ value }) => formatAmount(value),
  },
];

const EBChargePreviewModal = ({
  isOpen,
  title = 'Preview EB Charges',
  previewRows = [],
  skipped = [],
  errors = [],
  uploadFileName = '',
  onClose,
  onSaved,
}) => {
  const [saving, setSaving] = useState(false);

  const tableData = useMemo(
    () => withRoomGroupKey(previewRows),
    [previewRows]
  );

  const handleSave = async () => {
    if (!previewRows.length) {
      toastError('No invoices to apply');
      return;
    }
    setSaving(true);
    try {
      const items = previewRows.map(row => ({
        invoice_id: row.invoice_id,
        amount: row.eb_amount,
      }));
      const response = await apiPost('/invoicing/eb-bill/bulk-apply', { items });
      const data = response.data || {};
      if (data.success) {
        toastSuccess(`EB charges added to ${data.applied || 0} invoice(s)`);
        onSaved?.();
        onClose();
      } else {
        toastError('Failed to apply EB charges');
      }
    } catch (err) {
      toastError(err.response?.data?.detail || 'Failed to apply EB charges');
    } finally {
      setSaving(false);
    }
  };

  return (
    <RTModal
      isOpen={isOpen}
      onClose={saving ? undefined : onClose}
      title={title}
      size="xlarge"
      disableBackdropClick={saving}
      footer={(
        <>
          <RTButton variant="outline-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </RTButton>
          <RTButton
            variant="primary"
            onClick={handleSave}
            disabled={saving || previewRows.length === 0}
          >
            {saving ? 'Saving...' : `Save (${previewRows.length})`}
          </RTButton>
        </>
      )}
    >
      <div className="eb-modal-body-wrap">
        {saving && (
          <div className="eb-modal-saving-overlay">
            <RTSpinner
              message={`Applying EB charges to ${previewRows.length} invoice(s)...`}
              size={50}
            />
          </div>
        )}
        {uploadFileName && (
          <div className="eb-preview-file-name">
            <span className="eb-preview-file-name-label">Uploaded file:</span>
            <span className="eb-preview-file-name-value" title={uploadFileName}>
              {uploadFileName}
            </span>
          </div>
        )}
        <p className="eb-preview-summary">
          Review EB charges before saving. {previewRows.length} invoice(s) will be updated.
          {previewRows.length > PREVIEW_PAGE_SIZE && (
            <> Use the pagination controls below to browse all rows.</>
          )}
        </p>
        {(skipped.length > 0 || errors.length > 0) && (
          <div className="eb-preview-warnings">
            {skipped.length > 0 && <div>{skipped.length} invoice(s) skipped (no rate configured).</div>}
            {errors.length > 0 && <div>{errors.length} row(s) had errors and were excluded.</div>}
          </div>
        )}
        <div className="eb-preview-table-wrap">
          <Table
            columns={previewColumns}
            data={tableData}
            showPagination
            pageSize={PREVIEW_PAGE_SIZE}
            showSearch={false}
            exportable={false}
            disableSort
            rowGroupConfig={EB_BILL_ROW_GROUP_CONFIG}
            noDataText="No preview rows"
          />
        </div>
      </div>
    </RTModal>
  );
};

export default EBChargePreviewModal;
